/**
 * PaywallModal — polite "you've hit the free-tier limit" dialog with a clear
 * upgrade path. Shown when the backend returns HTTP 402 on any create/scan/import.
 *
 * Kept intentionally small: no context, no state machine. The parent decides
 * when to open/close by passing `open` + `onClose`; the message is the
 * server's 402 detail if present, else a friendly fallback.
 */
import { Link } from "react-router-dom";
import { Sparkle, X, ArrowUpRight } from "@phosphor-icons/react";

export default function PaywallModal({ open, onClose, message, upgradesEnabled = true, recipeCount, freeLimit }) {
  if (!open) return null;
  const showCount = Number.isInteger(recipeCount) && Number.isInteger(freeLimit) && freeLimit > 0;
  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center px-4 bg-rf-ink/70 backdrop-blur-sm"
      data-testid="paywall-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="paywall-title"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md bg-rf-cream rounded-3xl shadow-2xl border border-rf-ink/10 p-6 sm:p-8"
      >
        <button
          onClick={onClose}
          data-testid="paywall-close"
          aria-label="Close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full text-rf-ink-soft hover:bg-rf-ink/5 flex items-center justify-center"
        >
          <X size={20} weight="bold" />
        </button>

        <div className="w-14 h-14 rounded-2xl bg-rf-amber/40 flex items-center justify-center mb-4">
          <Sparkle size={28} weight="fill" className="text-rf-orange" />
        </div>

        {/* Progress teaser — makes the limit feel earned rather than punitive.
            Only shown when we actually know the user's current count/cap. */}
        {showCount && (
          <div
            className="mb-4 flex items-center gap-3 px-3 py-2 rounded-xl bg-rf-amber/25 border border-rf-amber/50"
            data-testid="paywall-count-teaser"
          >
            <span className="text-xs font-black uppercase tracking-wider text-rf-ink shrink-0">
              {Math.min(recipeCount, freeLimit)} / {freeLimit}
            </span>
            <span className="text-xs text-rf-ink-soft leading-snug">
              You&apos;ve saved <b>{Math.min(recipeCount, freeLimit)}</b> of your <b>{freeLimit}</b> free saved recipes — nicely done! (Scans &amp; previews stay unlimited.)
            </span>
          </div>
        )}

        <h2 id="paywall-title" className="font-display text-2xl sm:text-3xl font-black text-rf-ink leading-tight mb-3">
          You&apos;ve saved every free recipe.
        </h2>

        <p className="text-rf-ink-soft leading-relaxed mb-6" data-testid="paywall-message">
          {message
            || "Your free Taster account gets you a generous starter batch of saved recipes — and you\u2019ve saved them all. Scanning and previewing new recipes is still free; to keep saving new ones (and to unlock printing), pick a plan below. You can cancel any time."}
        </p>

        {upgradesEnabled ? (
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/pricing"
              onClick={onClose}
              data-testid="paywall-see-plans"
              className="btn-amber flex-1 justify-center"
            >
              See plans <ArrowUpRight size={16} weight="bold" />
            </Link>
            <button
              onClick={onClose}
              data-testid="paywall-later"
              className="btn-outline-ink flex-1 justify-center"
            >
              Not right now
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <p className="text-sm text-rf-ink">
              You&apos;ve used your free recipes. Upgrade to save more.
            </p>
            <Link to="/pricing" onClick={onClose} data-testid="paywall-upgrade-fallback" className="btn-amber w-full justify-center">
              See plans
            </Link>
            <button
              onClick={onClose}
              data-testid="paywall-later"
              className="btn-outline-ink w-full justify-center"
            >
              Got it
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
